import * as Sentry from '@sentry/angular';
import {
  type Metric,
  type MetricRatingThresholds,
  onCLS,
  onFCP,
  onINP,
  onLCP,
  onTTFB,
} from 'web-vitals';

import { environment } from '../../../environments/environment';

/**
 * Reporta Web Vitals (LCP, INP, CLS, FCP, TTFB) ao Sentry como
 * breadcrumbs + emite warning quando rating = 'poor'.
 *
 * O `browserTracingIntegration` do Sentry já coleta esses vitals
 * automaticamente como measurements em transactions de pageload. Este
 * módulo adiciona uma camada extra:
 *
 * 1. Breadcrumbs por métrica — permite ver no Sentry "qual era o LCP
 *    antes do erro X aparecer?" (contexto temporal).
 * 2. captureMessage('warning') quando rating = 'poor' — alerta sobre
 *    UX degradada mesmo sem erro JS.
 * 3. Console log em dev — feedback rápido durante desenvolvimento.
 *
 * Alvos da arch §6.1 (P75):
 *   LCP < 2.0s (limite 2.5s) | INP < 150ms (200ms) | CLS < 0.05 (0.1)
 *   FCP < 1.2s (1.8s)        | TTFB < 400ms (800ms)
 *
 * `web-vitals` usa thresholds Google CWV (`good`/`needs-improvement`/`poor`).
 */
export function initWebVitals(): void {
  onCLS(reportMetric);
  onINP(reportMetric);
  onLCP(reportMetric);
  onFCP(reportMetric);
  onTTFB(reportMetric);
}

function reportMetric(metric: Metric): void {
  const level: Sentry.SeverityLevel = ratingToLevel(metric.rating);

  Sentry.addBreadcrumb({
    category: 'web-vitals',
    type: 'info',
    level,
    message: `${metric.name}: ${metric.value.toFixed(metric.name === 'CLS' ? 4 : 0)}`,
    data: {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      navigationType: metric.navigationType,
    },
  });

  if (metric.rating === 'poor') {
    Sentry.captureMessage(
      `Web Vital "poor": ${metric.name} = ${metric.value}`,
      {
        level: 'warning',
        tags: {
          metric: metric.name,
          rating: metric.rating,
        },
        contexts: {
          webVital: {
            ...(metric as unknown as Record<string, unknown>),
          },
        },
      },
    );
  }

  if (!environment.production) {
    // eslint-disable-next-line no-console
    console.log(
      `[web-vitals] ${metric.name}: ${metric.value} (${metric.rating})`,
    );
  }
}

function ratingToLevel(
  rating: keyof MetricRatingThresholds | string | undefined,
): Sentry.SeverityLevel {
  switch (rating) {
    case 'good':
      return 'info';
    case 'needs-improvement':
      return 'warning';
    case 'poor':
      return 'error';
    default:
      return 'info';
  }
}
